// "How this skill works" — a shape-adaptive, hand-rolled card that renders whichever blocks a
// skill actually has (when-to-use · a takes→gives signature · an ordered step flow · an unordered
// set of moves · sub-commands · a collapsed show-me example · bundled files). There is NO fixed
// category, so divergent/dense skills degrade gracefully to their own subset. The "transformation"
// motif (left → gradient badge → right) is reused for both the abstract Takes/Gives signature and
// the concrete Given/Result example. Data comes from the server (`SkillDetail.workflow`), produced
// by the Sonnet workflow agent. Design was iterated in a screenshot-scored loop.

import { useState } from "react";

import { Point } from "@/components/Bullet";
import type { SkillWorkflowData } from "@/lib/types";

const GREEN_CARD = { background: "var(--radar-glow)", borderColor: "var(--radar-stroke-0)" } as React.CSSProperties;
const GREEN_TEXT = { color: "var(--radar-score-strong)" } as React.CSSProperties;

// ── inline icons (stroke = currentColor; matches the app's no-icon-library convention) ──
const Bolt = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 16 16" className={`h-3.5 w-3.5 ${className}`}><path d="M8.5 1L2 8.5h4L5.5 15L12 6.5H8L8.5 1z" fill="currentColor" /></svg>
);
const ArrowSm = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" stroke="currentColor" fill="none" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"><path d="M3 8h8M8 4l4 4-4 4" /></svg>
);
const Eye = () => (
  <svg viewBox="0 0 16 16" className="h-[15px] w-[15px] shrink-0 text-primary" stroke="currentColor" fill="none" strokeWidth={1.4}><path d="M1.5 8S3.7 3.5 8 3.5 14.5 8 14.5 8 12.3 12.5 8 12.5 1.5 8 1.5 8z" /><circle cx="8" cy="8" r="1.8" /></svg>
);
const IconCode = <svg viewBox="0 0 16 16" className="h-3 w-3 shrink-0" stroke="currentColor" fill="none" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round"><path d="M6 4L2.5 8L6 12M10 4l3.5 4L10 12" /></svg>;
const IconDoc = <svg viewBox="0 0 16 16" className="h-3 w-3 shrink-0" stroke="currentColor" fill="none" strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round"><path d="M4 2h5l3 3v9H4zM9 2v3h3M6 8.5h4M6 11h4" /></svg>;
const IconImg = <svg viewBox="0 0 16 16" className="h-3 w-3 shrink-0" stroke="currentColor" fill="none" strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 3h11v10h-11zM3 11l3-3 2.5 2.5M9 9.5l2-2 2 2" /><circle cx="6" cy="6" r="1" /></svg>;
const IconFile = <svg viewBox="0 0 16 16" className="h-3 w-3 shrink-0" stroke="currentColor" fill="none" strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round"><path d="M4 2h5l3 3v9H4zM9 2v3h3" /></svg>;
const fileIcon = (p: string) => {
  const e = (p.split(".").pop() || "").toLowerCase();
  if (["sh", "py", "js", "ts", "rb", "go", "json", "yaml", "yml"].includes(e)) return IconCode;
  if (["md", "txt", "rst", "csv"].includes(e)) return IconDoc;
  if (["png", "svg", "jpg", "jpeg", "gif", "pdf"].includes(e)) return IconImg;
  return IconFile;
};

// The transformation motif — left cell → gradient badge → right cell.
// Abstract when mono=false (Takes/Gives chips), concrete when mono=true (Given/Result data).
function Xform({ lk, lv, rk, rv, mono = false, flush = false }: { lk: string; lv: string[]; rk: string; rv: string[]; mono?: boolean; flush?: boolean }) {
  const Cell = ({ k, vals, out }: { k: string; vals: string[]; out: boolean }) => (
    <div className={`min-w-0 flex-1 rounded-xl border px-3.5 py-2.5 ${out ? "" : "border-white/10 bg-white/[0.02]"}`} style={out ? GREEN_CARD : undefined}>
      <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-text/45" style={out ? GREEN_TEXT : undefined}>{k}</div>
      {vals.length === 0 ? (
        <div className="text-[12.5px] text-text/30">—</div>
      ) : mono ? (
        <div className="font-mono text-[12px] leading-[1.65] text-text/80 [overflow-wrap:anywhere]">{vals.map((v, i) => <div key={i} className="whitespace-pre-wrap">{v}</div>)}</div>
      ) : (
        <div className={`flex flex-col gap-1 text-[12.5px] leading-snug [overflow-wrap:anywhere] ${out ? "" : "text-text/80"}`} style={out ? GREEN_TEXT : undefined}>
          {vals.map((v, i) => (vals.length > 1 ? <Point key={i}>{v}</Point> : <div key={i}>{v}</div>))}
        </div>
      )}
    </div>
  );
  return (
    <div className={`flex items-stretch ${flush ? "" : "mb-[22px]"}`}>
      <Cell k={lk} vals={lv} out={false} />
      <div
        className="relative z-[2] -mx-[9px] flex h-[30px] w-[30px] shrink-0 self-center items-center justify-center rounded-full text-white"
        style={{ background: "linear-gradient(120deg, var(--brand), var(--radar-stroke-0))", boxShadow: "0 3px 10px rgba(0,0,0,.16), 0 0 0 3px var(--bg)" }}
      ><ArrowSm /></div>
      <Cell k={rk} vals={rv} out={true} />
    </div>
  );
}

const Folder = () => (
  <svg viewBox="0 0 16 16" className="h-[15px] w-[15px] shrink-0 text-primary" stroke="currentColor" fill="none" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 4.2a1 1 0 0 1 1-1h3l1.5 1.6h6a1 1 0 0 1 1 1v5.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1z" /></svg>
);

// A closed-by-default disclosure — used for both "Show me" and the files list so a dense card
// stays scannable and the reader opens the detail they want.
function Disclosure({ icon, label, hint, children }: { icon: React.ReactNode; label: string; hint?: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-[22px] overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left text-[12.5px] font-semibold text-text/75 hover:bg-white/[0.03]"
      >
        {icon}
        {label}{hint && <span className="font-medium text-text/40"> {hint}</span>}
        <svg className={`ml-auto h-3.5 w-3.5 shrink-0 text-text/40 transition-transform duration-200 ${open ? "rotate-90" : ""}`} viewBox="0 0 16 16" stroke="currentColor" fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M6 4l4 4-4 4" /></svg>
      </button>
      {open && <div className="px-3.5 pb-3.5 pt-1">{children}</div>}
    </div>
  );
}

// Example — reveals the Given→Result example.
function ShowMe({ example }: { example: NonNullable<SkillWorkflowData["example"]> }) {
  return (
    <Disclosure icon={<Eye />} label="Example">
      <div className="mt-3">
        <Xform lk="Given" lv={example.given} rk="Result" rv={example.result} mono flush />
      </div>
    </Disclosure>
  );
}

function BlockHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2.5 text-[12.5px] font-semibold text-text/70">
      <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-primary" style={{ boxShadow: "0 0 0 3px var(--brand-tint)" }} />
      {children}
    </div>
  );
}

function FilesBlock({ files }: { files: NonNullable<SkillWorkflowData["files"]> }) {
  const groups: Record<string, typeof files> = {};
  for (const f of files) {
    const i = f.path.lastIndexOf("/");   // group by full directory so nested chips stay short
    const dir = i < 0 ? "./" : f.path.slice(0, i + 1);
    (groups[dir] = groups[dir] || []).push(f);
  }
  const nref = files.filter((f) => f.ref).length;
  return (
    <Disclosure icon={<Folder />} label={`Referenced files (${nref})`}>
      <div className="flex flex-col gap-[11px]">
        {Object.entries(groups).map(([dir, fs]) => (
          <div key={dir}>
            <div className="mb-[7px] font-mono text-[11px] font-semibold text-text/50">{dir}</div>
            <div className="flex flex-wrap gap-1.5">
              {fs.map((f) => {
                const base = f.path.slice(f.path.lastIndexOf("/") + 1);
                return (
                  <span key={f.path} className={`inline-flex max-w-full items-center gap-1.5 rounded-md border py-[3px] pl-[7px] pr-2.5 font-mono text-[11.5px] [overflow-wrap:anywhere] ${f.ref ? "text-text/85" : "border-white/10 bg-white/[0.02] text-text/70"}`} style={f.ref ? { borderColor: "var(--brand-ring)", background: "var(--brand-tint)" } : undefined}>
                    <span className={f.ref ? "text-primary" : "text-text/40"}>{fileIcon(f.path)}</span>{base}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {nref > 0 && (
        <div className="mt-2.5 flex items-center gap-2 text-[11px] text-text/45">
          <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-px text-text/70" style={{ border: "1px solid var(--brand-ring)" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />referenced
          </span>
          the skill actually calls these; the rest ship alongside
        </div>
      )}
    </Disclosure>
  );
}

// The shape-adaptive card. Renders only the blocks a skill actually has.
// `embedded` drops the card's own chrome + title (for use inside a modal that supplies them).
export function SkillWorkflow({ data, embedded = false }: { data: SkillWorkflowData; embedded?: boolean }) {
  const hasSteps = !!data.steps && data.steps.items.length > 0;
  const hasCmds = !!data.commands && data.commands.length > 0;
  const mix = (f: number) => `color-mix(in oklab, var(--brand), var(--radar-stroke-0) ${Math.round(f * 100)}%)`;

  return (
    <div className={embedded ? "" : "rounded-2xl border border-white/10 bg-white/[0.015] px-5 py-5"}>
      {!embedded && <div className="mb-3 font-display text-[15px] font-semibold tracking-[-0.01em] text-text">How this skill works</div>}

      {data.trigger && (
        <div className="mb-5 flex items-start gap-2 text-[13.5px] leading-normal text-text/85">
          <Bolt className="mt-0.5 shrink-0 text-primary" />
          <span className="min-w-0 [overflow-wrap:anywhere]"><span className="font-semibold text-primary">When to use — </span>{data.trigger}</span>
        </div>
      )}

      {data.io && <Xform lk="Input" lv={data.io.takes} rk="Output" rv={data.io.gives} />}

      {hasSteps && data.steps!.ordered && (
        <>
          {/* Phones: a vertical numbered list. The horizontal timeline below needs ~112px per step,
              so 3+ steps overflow and clip on a narrow screen — stack them instead. */}
          <ol className="flex flex-col gap-3 sm:hidden">
            {data.steps!.items.map((s, i) => (
              <li key={i} className="flex gap-3">
                <div className="relative z-[1] flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full font-mono text-[12px] font-bold text-white" style={{ background: "linear-gradient(135deg, var(--brand), var(--brand-hover))", boxShadow: "0 2px 7px rgba(225,100,46,.32)" }}>{i + 1}</div>
                <div className="min-w-0 pt-0.5 [overflow-wrap:anywhere]">
                  <div className="mb-[2px] font-display text-[13.5px] font-semibold tracking-[-0.01em] text-text">{s.title}</div>
                  {s.detail && <div className="text-[11.5px] leading-[1.4] text-text/[0.58]">{s.detail}</div>}
                </div>
              </li>
            ))}
          </ol>
          {/* sm+ : the horizontal timeline. */}
          <div className="hidden flex-col gap-[11px] overflow-x-auto sm:flex">
            <div className="flex items-center">
              {data.steps!.items.map((_, i) => {
                const n = data.steps!.items.length;
                return (
                  <div key={i} className="flex min-w-[112px] flex-1 items-center">
                    <div className="relative z-[1] flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full font-mono text-[12px] font-bold text-white" style={{ background: "linear-gradient(135deg, var(--brand), var(--brand-hover))", boxShadow: "0 2px 7px rgba(225,100,46,.32)" }}>{i + 1}</div>
                    {i < n - 1 && <div className="ml-[7px] h-[2.5px] flex-1 rounded-[3px] opacity-75" style={{ background: `linear-gradient(90deg, ${mix(i / (n - 1))}, ${mix((i + 1) / (n - 1))})` }} />}
                  </div>
                );
              })}
            </div>
            <div className="flex">
              {data.steps!.items.map((s, i) => (
                <div key={i} className="min-w-[112px] flex-1 pr-[18px] [overflow-wrap:anywhere]">
                  <div className="mb-[3px] font-display text-[13.5px] font-semibold tracking-[-0.01em] text-text">{s.title}</div>
                  {s.detail && <div className="text-[11.5px] leading-[1.4] text-text/[0.58]">{s.detail}</div>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {hasSteps && !data.steps!.ordered && (
        <div className="mt-[22px]">
          {data.steps!.label && <BlockHead>{data.steps!.label}</BlockHead>}
          <div className="flex flex-wrap gap-2">
            {data.steps!.items.map((s, i) => <span key={i} className="rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-[5px] text-[12px] text-text/75 [overflow-wrap:anywhere]">{s.title}</span>)}
          </div>
        </div>
      )}

      {hasCmds && (
        <div className="mt-[22px] grid gap-2 sm:grid-cols-2">
          {data.commands!.map((c, i) => (
            <div key={i} className="min-w-0 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <code className="font-mono text-[12.5px] font-semibold text-primary [overflow-wrap:anywhere]">{c.name}</code>
              <p className="mt-[3px] text-[12px] leading-[1.4] text-text/[0.58] [overflow-wrap:anywhere]">{c.purpose}</p>
            </div>
          ))}
        </div>
      )}

      {data.files && data.files.length > 0 && <FilesBlock files={data.files} />}

      {data.example && <ShowMe example={data.example} />}
    </div>
  );
}
